// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The active machine, as React state (M9-32).
//
// Two stores with two shapes, which is why this is a hook rather than one `useState`. The **choice**
// is a word in `localStorage` and is readable before the first paint; the **draft** is a machine
// definition in IndexedDB and arrives a moment later. Treating them as one value would mean either
// waiting on the disk before drawing the front door — the one page whose whole design is "a machine,
// immediately" — or flashing the flagship at an author who chose their own.
//
// So the choice is read synchronously and the draft is awaited, and `ready` is what says the pair
// can be trusted:
//
//   * A browser on the flagship — every stranger, and every author who switched back — is **ready
//     at once**. The draft still loads, but only to decide whether there is a control to draw, and
//     a control that appears a moment after the machine costs nothing.
//   * A browser on the draft waits for it. That is an author, on their own machine, and a moment of
//     skeleton is better than a moment of somebody else's cabinet.
//
// `useSyncExternalStore` for the choice, for `usePlayFocus`'s reason: the Builder can set it from a
// mount effect, and a `useState` plus a subscription in an effect leaves a window between the two
// that such a set falls into.

import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  readActiveChoice,
  readDraft,
  watchActiveChoice,
  type ActiveChoice,
  type StoredDraft,
} from './activeMachine'

/** A server has no browser storage, and the front door it prerenders is the flagship's. */
function onTheServer(): ActiveChoice {
  return 'flagship'
}

function currentChoice(): ActiveChoice {
  return readActiveChoice()
}

export interface ActiveMachineState {
  readonly choice: ActiveChoice
  readonly draft: StoredDraft | undefined
  /** Has the disk answered, or is the answer above simply "not yet"? */
  readonly loaded: boolean
  /** May the caller mount a machine now? See the file header for the two cases. */
  readonly ready: boolean
}

/**
 * What this browser is playing on the front door, kept current.
 *
 * The draft is read **once per mount**, deliberately. It changes only in the Builder, which is a
 * different route: a stage that re-read it would be a stage that could swap the machine under a
 * ball, and the draft's own contract — the Builder's, since M9-17 — is that a definition is taken
 * once and editing is not live.
 */
export function useActiveMachine(): ActiveMachineState {
  const choice = useSyncExternalStore(watchActiveChoice, currentChoice, onTheServer)
  const [draft, setDraft] = useState<StoredDraft | undefined>(undefined)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    void readDraft().then((held) => {
      if (!live) return
      setDraft(held)
      setLoaded(true)
    })
    return () => {
      live = false
    }
  }, [])

  return { choice, draft, loaded, ready: loaded || choice === 'flagship' }
}
